import { HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInCheckInClusterPhotoDto, vehicleCheckInCheckInClusterPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface CheckInClusterPhotoDialogProps {
  initialValues: VehicleCheckInCheckInClusterPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInCheckInClusterPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function CheckInClusterPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: CheckInClusterPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInCheckInClusterPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Speedometer Before Modification Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Speedometer Before Modification'
                      file={values.vehicleCheckInCheckInClusterPhotoFile}
                      placeholderSrc='/5 Check In Cluster.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='check-in-cluster-browse'
                    cameraInputId='check-in-cluster-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInCheckInClusterPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      Also use this photo for Odometer Before Modification photo upload?
                    </Typography>
                    <RadioGroup
                      name='alsoUseForOdometerBeforeModificationPhoto'
                      onChange={event =>
                        setFieldValue('alsoUseForOdometerBeforeModificationPhoto', event.target.value === 'true')
                      }
                      row
                      value={String(values.alsoUseForOdometerBeforeModificationPhoto ?? true)}
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            checked={values.alsoUseForOdometerBeforeModificationPhoto ?? true}
                            disabled={isSubmitting || isLoading}
                          />
                        }
                        label='Yes'
                        value='true'
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={values.alsoUseForOdometerBeforeModificationPhoto === false}
                            disabled={isSubmitting || isLoading}
                          />
                        }
                        label='No'
                        value='false'
                      />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Speedometer Before Modification Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || “Delete Uploaded Photo” can only be pressed if a user has uploaded an image. || “Submit” can only be
                      pressed if a user has uploaded an image. Pressing “Submit” will associate the user-uploaded image with the
                      vehicle record and return the user to the Check-In Photos gallery screen. || Pressing “Previous” goes to
                      the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
